import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MoveHorizontal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ComparePane({ pair }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(Math.max(2, Math.min(98, ((clientX - r.left) / r.width) * 100)));
  };
  const onDown = (e) => { dragging.current = true; update(e.clientX); };
  const onMove = (e) => { if (dragging.current) update(e.clientX); };
  const stop = () => { dragging.current = false; };
  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(2, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(98, p + 4));
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-compare"
      className="relative aspect-[16/10] sm:aspect-[16/9] w-full overflow-hidden rounded-lg border-2 border-[#0C2E2B] select-none cursor-ew-resize bg-zinc-200"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      role="slider"
      aria-label="Drag to compare before and after"
      aria-valuenow={Math.round(pos)}
      tabIndex={0}
      onKeyDown={onKey}
    >
      <img src={pair.after_url} alt={`${pair.title} — after`} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={pair.before_url} alt={`${pair.title} — before`} className="absolute inset-0 h-full object-cover" style={{ width: ref.current ? ref.current.clientWidth : "100%", maxWidth: "none" }} draggable={false} />
      </div>
      <span className="absolute top-3 left-3 px-2 py-1 rounded bg-[#0C2E2B]/90 text-[#FFDF00] text-[10px] font-tactical uppercase tracking-[0.2em]">Before</span>
      <span className="absolute top-3 right-3 px-2 py-1 rounded bg-[#FFDF00] text-[#0C2E2B] text-[10px] font-tactical uppercase tracking-[0.2em] font-bold">After</span>
      <div className="absolute inset-y-0 w-0.5 bg-[#FFDF00] shadow-[0_0_0_1px_rgba(12,46,43,.6)]" style={{ left: `${pos}%` }} data-testid="before-after-handle">
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-[#FFDF00] border-2 border-[#0C2E2B] grid place-items-center shadow-lg transition-transform hover:scale-110">
          <MoveHorizontal className="w-5 h-5 text-[#0C2E2B]" strokeWidth={2.5} />
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  const [pairs, setPairs] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    axios.get(`${API}/before-after`).then(({ data }) => setPairs(data.items || [])).catch(() => {});
  }, []);

  if (!pairs.length) return null;
  const pair = pairs[Math.min(active, pairs.length - 1)];

  return (
    <section id="before-after" data-testid="before-after-section" className="py-20 bg-[#0C2E2B] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 items-center">
        <div>
          <div className="font-tactical text-xs uppercase tracking-[0.25em] text-[#FFDF00]">Proof, not promises</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl mt-2 leading-tight">Drag to see the difference</h2>
          <p className="text-white/70 mt-4 text-sm md:text-base">Real jobs from real neighbors. Slide the handle to compare where we started and where we left it.</p>
          <div className="mt-6">
            <div className="font-display font-black text-xl text-[#FFDF00]" data-testid="before-after-title">{pair.title}</div>
            {pair.caption && <p className="text-white/70 text-sm mt-1" data-testid="before-after-caption">{pair.caption}</p>}
          </div>
          {pairs.length > 1 && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="before-after-tabs">
              {pairs.map((p, i) => (
                <button
                  key={p.id}
                  data-testid={`before-after-tab-${i}`}
                  onClick={() => setActive(i)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${i === active ? "bg-[#FFDF00] text-[#0C2E2B]" : "bg-white/10 text-white hover:bg-white/20"}`}
                >
                  {p.title}
                </button>
              ))}
            </div>
          )}
          <a href="#quote" data-testid="before-after-cta" className="inline-flex mt-8 items-center justify-center rounded-md bg-[#FFDF00] text-[#0C2E2B] font-black px-6 py-3 hover:bg-[#E5C900] transition-colors">
            Get my instant quote
          </a>
        </div>
        <ComparePane key={pair.id} pair={pair} />
      </div>
    </section>
  );
}
